import { useState, useEffect } from 'react';
import { useEditLock } from '../hooks/useEditLock';
import { LockBanner } from '../components/permissions/LockBanner';
import { ReadOnlyGuard } from '../components/permissions/ReadOnlyGuard';
import { broadcastDataChange } from '../lib/dataBus';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, CircleDot, Receipt, Plus, Loader2, Euro, Calendar, CreditCard, CheckCircle2, FileDown, Eye, Download, X, Send, Mail, MoreVertical, Pencil, Check, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUT_ENVOI = {
    a_envoyer: { label: 'A envoyer', color: 'bg-red-100 text-red-700 border-red-200' },
    envoyee: { label: 'Envoyee', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const STATUT_PAIEMENT = {
    impayee: { label: 'Impayee', color: 'bg-red-100 text-red-700 border-red-200' },
    partiellement_payee: { label: 'Partiellement payee', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    payee: { label: 'Payee', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const STATUS_BORDER = {
    impayee: 'border-red-400',
    partiellement_payee: 'border-amber-300',
    payee: 'border-emerald-400',
};

const TYPE_PAIEMENT_LABELS = {
    cheque_o: 'Cheque O',
    cheque_so: 'Cheque SO',
    virement: 'Virement',
    espece: 'Espece',
};

function formatDate(d) { if (!d) return '-'; try { return format(new Date(d), 'dd/MM/yyyy', { locale: fr }); } catch { return d; } }
function formatDateTime(d) { if (!d) return '-'; try { return format(new Date(d), 'dd/MM/yyyy HH:mm', { locale: fr }); } catch { return d; } }

export default function FacturationDetailPage() {
    const { factureId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const backTo = location.state?.from === 'recap' ? '/facturation/recap' : '/facturation';
    const lock = useEditLock('facture', factureId, !!factureId);
    const [facture, setFacture] = useState(null);
    const [paiements, setPaiements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ date_paiement: '', montant: '', type_paiement: '', remarque: '' });
    const [generating, setGenerating] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [sending, setSending] = useState(false);
    const [emailForm, setEmailForm] = useState({ destinataire: '', sujet: '', message: '' });
    const [civilite, setCivilite] = useState('Monsieur');
    const [showFactureMenu, setShowFactureMenu] = useState(false);
    const [editingPrix, setEditingPrix] = useState(false);
    const [editPrixValue, setEditPrixValue] = useState('');

    const fetchData = () => {
        setLoading(true);
        Promise.all([
            axios.get(`${API}/api/factures/${factureId}`, { withCredentials: true }),
            axios.get(`${API}/api/factures/${factureId}/paiements`, { withCredentials: true }),
        ]).then(([fRes, pRes]) => {
            setFacture(fRes.data);
            setPaiements(pRes.data);
        }).catch(() => toast.error('Erreur de chargement'))
        .finally(() => setLoading(false));
    };

    useEffect(() => { fetchData(); }, [factureId]);

    const handleStatutEnvoi = async (val) => {
        try {
            await axios.patch(`${API}/api/factures/${factureId}/statut-envoi?statut=${val}`, {}, { withCredentials: true });
            toast.success('Statut mis a jour');
            broadcastDataChange('facture', factureId, { field: 'statut_envoi', value: val });
            fetchData();
        } catch { toast.error('Erreur'); }
    };

    const handleAddPaiement = async () => {
        if (!form.date_paiement || !form.montant || !form.type_paiement) {
            toast.error('Veuillez remplir la date, le montant et le type');
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/api/factures/${factureId}/paiements`, {
                ...form,
                montant: parseFloat(form.montant),
            }, { withCredentials: true });
            toast.success('Paiement ajoute');
            broadcastDataChange('facture', factureId, { field: 'paiement' });
            setShowForm(false);
            setForm({ date_paiement: '', montant: '', type_paiement: '', remarque: '' });
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };

    const handleDeletePaiement = async (paiementId) => {
        if (!window.confirm('Supprimer ce paiement ?')) return;
        try {
            await axios.delete(`${API}/api/factures/${factureId}/paiements/${paiementId}`, { withCredentials: true });
            toast.success('Paiement supprime');
            broadcastDataChange('facture', factureId, { field: 'paiement' });
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    const handlePreviewPdf = async () => {
        setGenerating(true);
        try {
            const res = await axios.get(`${API}/api/factures/${factureId}/pdf`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            setPdfUrl(url);
        } catch { toast.error('Erreur lors de la generation du PDF'); }
        finally { setGenerating(false); }
    };

    const handleDownloadPdf = () => {
        if (!pdfUrl) return;
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.download = `Facture-${facture?.numero_facture || ''}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success('Facture PDF telechargee');
    };

    const handleClosePdf = () => {
        if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
    };

    const buildEmailMessage = (civ) => {
        const nom = facture?.client_nom || '';
        const dateDem = facture?.date_demenagement ? formatDate(facture.date_demenagement) : '___';
        return `Bonjour ${civ} ${nom},\n\nNous espérons que notre équipe a été à la hauteur de vos attentes et que votre déménagement s'est déroulé dans les meilleures conditions.\n\nNous vous prions de bien vouloir trouver ci-joint votre facture, faisant suite à la prestation réalisée le ${dateDem}.\n\nNous vous remercions d'avoir choisi Big Déménagement pour vous accompagner dans votre projet de déménagement et restons à votre entière disposition pour toute question ou besoin complémentaire.\n\nNous vous souhaitons une excellente installation ainsi qu'une très bonne journée.\n\nCordialement,\nL'équipe Big Déménagement`;
    };

    const handleOpenEmailModal = () => {
        const civ = 'Monsieur';
        setCivilite(civ);
        setEmailForm({
            destinataire: facture?.client_email || '',
            sujet: `Facture n° ${facture?.numero_facture || ''} - BIG DEMENAGEMENT`,
            message: buildEmailMessage(civ),
        });
        setShowEmailModal(true);
    };

    const handleCiviliteChange = (newCiv) => {
        setCivilite(newCiv);
        setEmailForm(prev => ({ ...prev, message: buildEmailMessage(newCiv) }));
    };

    const handleSendEmail = async () => {
        if (!emailForm.destinataire || !emailForm.sujet) {
            toast.error('Veuillez remplir le destinataire et le sujet');
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/api/factures/${factureId}/send-email`, emailForm, { withCredentials: true });
            toast.success('Facture envoyee par email');
            setShowEmailModal(false);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur d\'envoi'); }
        finally { setSending(false); }
    };

    const handleSavePrix = async () => {
        const val = parseFloat(editPrixValue);
        if (isNaN(val) || val < 0) { toast.error('Montant invalide'); return; }
        try {
            await axios.patch(`${API}/api/factures/${factureId}/prix-ttc`, { prix_ttc: val }, { withCredentials: true });
            toast.success('Prix TTC mis a jour');
            broadcastDataChange('facture', factureId, { field: 'prix_ttc', value: val });
            broadcastDataChange('client');
            setEditingPrix(false);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" /></div>;
    if (!facture) return <div className="text-center py-24 text-slate-500">Facture non trouvee</div>;

    const paiInfo = STATUT_PAIEMENT[facture.statut_paiement] || STATUT_PAIEMENT.impayee;
    const borderColor = STATUS_BORDER[facture.statut_paiement] || 'border-red-400';
    const envInfo = STATUT_ENVOI[facture.statut_envoi] || STATUT_ENVOI.a_envoyer;

    return (
        <div className="flex flex-col h-full" data-testid="facturation-detail-page" onClick={() => showFactureMenu && setShowFactureMenu(false)}>
            {lock.blocked && <LockBanner userName={lock.user_name} expiresAt={lock.expires_at} />}
            <ReadOnlyGuard module="facturation" lockBlocked={lock.blocked}>
            {/* Header */}
            <div className="sticky top-0 z-50 bg-white border-b px-4 md:px-6 py-3 md:py-4">
                <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <Button variant="outline" size="sm" onClick={() => navigate(backTo)} data-testid="facture-back-btn">
                            <ArrowLeft size={16} className="mr-2" />
                            Retour
                        </Button>
                        <h1 className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Apercu facturation — {facture.client_nom}
                        </h1>
                    </div>
                    <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={handleOpenEmailModal} data-testid="facture-send-email-btn">
                        <Send size={16} className="mr-2" />
                        Envoyer la facture
                    </Button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
                <div className="max-w-5xl mx-auto space-y-6">
                    {/* Section 1: Statut facturation */}
                    <Card className={`bg-blue-50 border-2 ${borderColor}`}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-800">
                                <CircleDot size={16} />
                                1. Statut de la facturation
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap items-center gap-4">
                                {/* Statut paiement */}
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-slate-500">Paiement :</span>
                                    <Badge variant="outline" className={`${paiInfo.color} border text-sm px-3 py-1`}>{paiInfo.label}</Badge>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section 2: Paiements */}
                    <Card className="border-slate-200" data-testid="facture-paiements-section">
                        <CardHeader className="pb-2 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center justify-between text-slate-700">
                                <div className="flex items-center gap-2">
                                    <Euro size={16} className="text-[#2e7cd6]" />
                                    2. Paiements
                                    {paiements.length > 0 && (
                                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 ml-1">{paiements.length}</Badge>
                                    )}
                                </div>
                                <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={() => { setShowForm(true); setForm({ date_paiement: '', montant: '', type_paiement: '', remarque: '' }); }} data-testid="facture-add-paiement-btn">
                                    <Plus size={14} className="mr-1.5" />
                                    Ajouter un paiement
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            {/* Formulaire ajout paiement */}
                            {showForm && (
                                <div className="p-4 rounded-lg border border-[#2e7cd6] bg-blue-50/20 mb-4" data-testid="paiement-form">
                                    <p className="text-sm font-medium text-slate-700 mb-3">Nouveau paiement</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                                        <div>
                                            <label className="text-xs text-slate-400 block mb-1">Date du paiement</label>
                                            <Input type="date" value={form.date_paiement} onChange={e => setForm(p => ({ ...p, date_paiement: e.target.value }))} className="h-8 text-sm" data-testid="paiement-date" />
                                        </div>
                                        <div>
                                            <label className="text-xs text-slate-400 block mb-1">Montant (EUR)</label>
                                            <Input type="number" step="0.01" min="0" value={form.montant} onChange={e => setForm(p => ({ ...p, montant: e.target.value }))} className="h-8 text-sm" placeholder="0.00" data-testid="paiement-montant" />
                                        </div>
                                        <div>
                                            <label className="text-xs text-slate-400 block mb-1">Type de paiement</label>
                                            <Select value={form.type_paiement} onValueChange={v => setForm(p => ({ ...p, type_paiement: v }))}>
                                                <SelectTrigger className="h-8 text-sm" data-testid="paiement-type">
                                                    <SelectValue placeholder="Choisir" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Object.entries(TYPE_PAIEMENT_LABELS).map(([v, label]) => (
                                                        <SelectItem key={v} value={v}>{label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <label className="text-xs text-slate-400 block mb-1">Remarque</label>
                                            <Input value={form.remarque} onChange={e => setForm(p => ({ ...p, remarque: e.target.value }))} className="h-8 text-sm" placeholder="Optionnel" data-testid="paiement-remarque" />
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Annuler</Button>
                                        <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={handleAddPaiement} disabled={saving} data-testid="paiement-confirm-btn">
                                            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                                            Confirmer
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Historique — grille style devis */}
                            {paiements.length === 0 ? (
                                <p className="text-sm text-slate-400 italic text-center py-6">Aucun paiement enregistre</p>
                            ) : (
                                <div className="overflow-hidden rounded-lg border border-slate-200">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-slate-50 border-b border-slate-200">
                                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                                                <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Montant</th>
                                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Remarque</th>
                                                <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Par</th>
                                                <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paiements.map((p, i) => (
                                                <tr key={p.id} className={`border-b border-slate-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`} data-testid={`paiement-${p.id}`}>
                                                    <td className="px-4 py-3 text-slate-700">{formatDate(p.date_paiement)}</td>
                                                    <td className="px-4 py-3">
                                                        <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600 border-slate-200">{TYPE_PAIEMENT_LABELS[p.type_paiement] || p.type_paiement}</Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-semibold text-emerald-700">{p.montant.toFixed(2)} EUR</td>
                                                    <td className="px-4 py-3 text-slate-500 italic text-xs">{p.remarque || '-'}</td>
                                                    <td className="px-4 py-3 text-right text-xs text-slate-400">{p.cree_par}</td>
                                                    <td className="px-2 py-3 text-right">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-7 w-7"
                                                            onClick={() => handleDeletePaiement(p.id)}
                                                            title="Supprimer ce paiement"
                                                            data-testid={`paiement-delete-${p.id}`}
                                                        >
                                                            <Trash2 size={14} className="text-red-500" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Section 3: Facture */}
                    {(() => {
                        const isEnvoyee = facture.statut_envoi === 'envoyee';
                        const factureBorder = isEnvoyee ? 'border-emerald-400' : 'border-red-400';
                        const totalPaye = paiements.reduce((sum, p) => sum + (p.montant || 0), 0);
                        return (
                            <Card className={`border-2 ${factureBorder} relative`} data-testid="facture-section">
                                <CardHeader className="pb-2 border-b border-slate-100">
                                    <CardTitle className="text-sm font-semibold flex items-center justify-between text-slate-700">
                                        <div className="flex items-center gap-2">
                                            <Receipt size={16} className="text-[#2e7cd6]" />
                                            3. Facture
                                            <Badge variant="outline" className={`ml-1 text-xs border ${isEnvoyee ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-red-100 text-red-700 border-red-300'}`}>
                                                {isEnvoyee ? 'Envoyee' : 'Non envoyee'}
                                            </Badge>
                                        </div>
                                        <div className="relative">
                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); setShowFactureMenu(!showFactureMenu); }} data-testid="facture-menu-btn">
                                                <MoreVertical size={16} className="text-slate-500" />
                                            </Button>
                                            {showFactureMenu && (
                                                <div className="absolute right-0 top-9 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1 w-44" data-testid="facture-menu-dropdown">
                                                    <button
                                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                                        onClick={() => { setShowFactureMenu(false); handlePreviewPdf(); }}
                                                        data-testid="facture-menu-preview"
                                                    >
                                                        <Eye size={14} className="text-[#2e7cd6]" />
                                                        Apercu
                                                    </button>
                                                    <button
                                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                                        onClick={async () => {
                                                            setShowFactureMenu(false);
                                                            setGenerating(true);
                                                            try {
                                                                const res = await axios.get(`${API}/api/factures/${factureId}/pdf`, { withCredentials: true, responseType: 'blob' });
                                                                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                                                const a = document.createElement('a');
                                                                a.href = url;
                                                                a.download = `Facture-${facture?.numero_facture || ''}.pdf`;
                                                                document.body.appendChild(a);
                                                                a.click();
                                                                a.remove();
                                                                window.URL.revokeObjectURL(url);
                                                                toast.success('Facture PDF telechargee');
                                                            } catch { toast.error('Erreur'); }
                                                            finally { setGenerating(false); }
                                                        }}
                                                        data-testid="facture-menu-download"
                                                    >
                                                        <Download size={14} className="text-[#2e7cd6]" />
                                                        Telecharger
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="pt-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-3 gap-x-6">
                                        <div>
                                            <span className="text-xs text-slate-400">Prix TTC</span>
                                            <div className="flex items-center gap-1.5">
                                                {editingPrix ? (
                                                    <>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={editPrixValue}
                                                            onChange={e => setEditPrixValue(e.target.value)}
                                                            className="h-7 w-28 text-sm"
                                                            autoFocus
                                                            onKeyDown={e => { if (e.key === 'Enter') handleSavePrix(); if (e.key === 'Escape') setEditingPrix(false); }}
                                                            data-testid="edit-prix-input"
                                                        />
                                                        <button onClick={handleSavePrix} className="text-emerald-600 hover:text-emerald-700" data-testid="edit-prix-save">
                                                            <Check size={16} />
                                                        </button>
                                                        <button onClick={() => setEditingPrix(false)} className="text-slate-400 hover:text-slate-600" data-testid="edit-prix-cancel">
                                                            <X size={14} />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="text-sm font-medium text-slate-800">{facture.prix_ttc?.toFixed(2) || '0.00'} EUR</p>
                                                        <button onClick={() => { setEditPrixValue(facture.prix_ttc?.toFixed(2) || '0'); setEditingPrix(true); }} className="text-slate-400 hover:text-[#2e7cd6] transition-colors" data-testid="edit-prix-btn">
                                                            <Pencil size={13} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-400">Regle</span>
                                            <p className="text-sm font-medium text-emerald-600">{totalPaye.toFixed(2)} EUR</p>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-400">Solde du</span>
                                            <p className={`text-sm font-medium ${facture.solde_du > 0 ? 'text-red-600' : facture.solde_du < 0 ? 'text-blue-600' : 'text-emerald-600'}`}>{facture.solde_du?.toFixed(2) || '0.00'} EUR</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                                        <span className="text-xs text-slate-400">N Facture :</span>
                                        <span className="font-mono text-sm font-medium text-slate-800">{facture.numero_facture}</span>
                                        <span className="text-xs text-slate-400 ml-2">N Devis : {facture.numero_devis}</span>
                                        <span className="text-xs text-slate-400 ml-2">Date dem. : {formatDate(facture.date_demenagement)}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })()}
                </div>
            </div>

            {/* PDF Preview Modal */}
            {pdfUrl && (
                <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="pdf-preview-modal">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Apercu — Facture {facture?.numero_facture}
                            </h2>
                            <div className="flex items-center gap-2">
                                <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={handleDownloadPdf} data-testid="pdf-download-btn">
                                    <Download size={15} className="mr-1.5" />
                                    Telecharger
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleClosePdf} data-testid="pdf-close-btn">
                                    <X size={15} />
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <iframe src={pdfUrl} className="w-full h-full border-0" title="Apercu facture" />
                        </div>
                    </div>
                </div>
            )}

            {/* Email Send Modal */}
            {showEmailModal && (
                <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="email-modal">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                <Mail size={18} className="text-[#2e7cd6]" />
                                Envoyer la facture par email
                            </h2>
                            <Button variant="outline" size="sm" onClick={() => setShowEmailModal(false)} data-testid="email-close-btn">
                                <X size={15} />
                            </Button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-slate-500 block mb-1">Civilite</label>
                                    <Select value={civilite} onValueChange={handleCiviliteChange}>
                                        <SelectTrigger className="h-9 text-sm" data-testid="email-civilite">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Monsieur">Monsieur</SelectItem>
                                            <SelectItem value="Madame">Madame</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-500 block mb-1">Destinataire</label>
                                    <Input
                                        type="email"
                                        value={emailForm.destinataire}
                                        onChange={e => setEmailForm(p => ({ ...p, destinataire: e.target.value }))}
                                        placeholder="email@client.com"
                                        className="h-9 text-sm"
                                        data-testid="email-destinataire"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-500 block mb-1">Sujet</label>
                                <Input
                                    value={emailForm.sujet}
                                    onChange={e => setEmailForm(p => ({ ...p, sujet: e.target.value }))}
                                    className="h-9 text-sm"
                                    data-testid="email-sujet"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-500 block mb-1">Message</label>
                                <Textarea
                                    value={emailForm.message}
                                    onChange={e => setEmailForm(p => ({ ...p, message: e.target.value }))}
                                    rows={10}
                                    className="text-sm resize-none"
                                    data-testid="email-message"
                                />
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
                                <p className="text-xs text-[#2e7cd6] font-semibold flex items-center gap-1.5">
                                    <FileDown size={14} />
                                    1 facture PDF en piece jointe
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
                            <Button variant="outline" size="sm" onClick={() => setShowEmailModal(false)}>Annuler</Button>
                            <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={handleSendEmail} disabled={sending} data-testid="email-send-btn">
                                {sending ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Send size={15} className="mr-1.5" />}
                                Envoyer
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            </ReadOnlyGuard>
        </div>
    );
}
